export type SessionState = 'idle' | 'recording' | 'processing' | 'speaking' | 'error';

export type EvaluationUpdate = {
  dimension: string;
  score: number;
  justification: string;
};

export type VoiceSessionCallbacks = {
  onStateChange: (state: SessionState) => void;
  onTranscript: (text: string, role: 'user' | 'assistant') => void;
  onError: (error: string) => void;
  onSpeakingStart: () => void;
  onSpeakingEnd: () => void;
};

export type VoiceSessionConfig = {
  // Se llama con el TEXTO transcripto cuando el usuario termina de hablar —
  // devuelve la respuesta del asistente. (STT ahora es del navegador: Web
  // Speech API, sin API keys — migrado de MediaRecorder+Whisper.)
  onProcessMessage: (text: string) => Promise<string>;
  // Called to convert text to speech — returns base64 audio
  onSynthesizeSpeech: (text: string) => Promise<{ data: string; mimeType: string }>;
  callbacks: VoiceSessionCallbacks;
};

/**
 * Loop de voz por turnos: escuchar (SpeechRecognition, es-AR) → procesar →
 * hablar (TTS Polly) → volver a escuchar. `continuous: false` hace que el
 * navegador cierre la escucha solo al detectar fin de frase (reemplaza el VAD
 * casero de silencio que necesitaba MediaRecorder).
 */
export class VoiceSession {
  private recognition: SpeechRecognition | null = null;
  private destroyed = false;
  private muted = false;
  private state: SessionState = 'idle';
  private config: VoiceSessionConfig;
  private callbacks: VoiceSessionCallbacks;
  private currentAudio: HTMLAudioElement | null = null;
  private pendingText = '';

  constructor(config: VoiceSessionConfig) {
    this.config = config;
    this.callbacks = config.callbacks;
  }

  private setState(s: SessionState) {
    this.state = s;
    this.callbacks.onStateChange(s);
  }

  getState() { return this.state; }

  static isSupported(): boolean {
    return typeof window !== 'undefined' && !!(window.SpeechRecognition || window.webkitSpeechRecognition);
  }

  async start() {
    if (this.destroyed) return;
    if (!VoiceSession.isSupported()) {
      this.callbacks.onError('speech_not_supported');
      this.setState('error');
      return;
    }
    this.startListening();
  }

  private startListening() {
    if (this.destroyed || this.muted) return;

    const SpeechRecognitionAPI = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new SpeechRecognitionAPI();
    rec.lang = 'es-AR';
    rec.continuous = false;      // el navegador corta solo al fin de la frase
    rec.interimResults = true;
    this.pendingText = '';

    rec.onresult = (event: SpeechRecognitionEvent) => {
      let finalText = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) finalText += event.results[i][0].transcript;
      }
      if (finalText) this.pendingText += finalText;
    };

    rec.onerror = (event: any) => {
      if (this.destroyed) return;
      if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
        this.callbacks.onError('microphone_denied');
        this.setState('error');
        return;
      }
      // 'no-speech' / 'aborted' → simplemente volver a escuchar (lo maneja onend)
    };

    rec.onend = () => {
      if (this.destroyed) return;
      const text = this.pendingText.trim();
      if (text) void this.handleUtterance(text);
      else if (!this.muted && this.state === 'recording') this.startListening();
    };

    this.recognition = rec;
    rec.start();
    this.setState('recording');
  }

  private async handleUtterance(text: string) {
    this.setState('processing');
    this.callbacks.onTranscript(text, 'user');

    try {
      const replyText = await this.config.onProcessMessage(text);
      if (!replyText || this.destroyed) {
        if (!this.destroyed) this.startListening();
        return;
      }

      this.setState('speaking');
      this.callbacks.onSpeakingStart();
      try {
        const { data: audioData, mimeType: audioMime } = await this.config.onSynthesizeSpeech(replyText);
        await this.playAudio(audioData, audioMime);
      } catch {
        // Fallback to browser TTS
        await this.speakBrowser(replyText);
      }
      this.callbacks.onSpeakingEnd();
    } catch (err) {
      this.callbacks.onError(err instanceof Error ? err.message : 'Processing failed');
    }

    if (!this.destroyed) this.startListening();
  }

  private playAudio(base64: string, mimeType: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const binary = atob(base64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      const blob = new Blob([bytes], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      this.currentAudio = audio;
      audio.onended = () => { URL.revokeObjectURL(url); resolve(); };
      audio.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Audio playback failed')); };
      audio.play().catch(reject);
    });
  }

  private speakBrowser(text: string): Promise<void> {
    return new Promise((resolve) => {
      if (!window.speechSynthesis) { resolve(); return; }
      const utt = new SpeechSynthesisUtterance(text);
      utt.lang = 'es-AR';
      utt.onend = () => resolve();
      utt.onerror = () => resolve();
      window.speechSynthesis.speak(utt);
    });
  }

  mute(muted: boolean) {
    this.muted = muted;
    if (muted) {
      this.recognition?.abort();
    } else if (this.state === 'recording' || this.state === 'idle') {
      this.startListening();
    }
  }

  destroy() {
    this.destroyed = true;
    this.recognition?.abort();
    this.currentAudio?.pause();
    window.speechSynthesis?.cancel();
    this.setState('idle');
  }
}
